import React, { useState, useEffect } from 'react';
import StyledModal from '../../../components/ui/StyledModal';
import StyledTable from '../../../components/ui/StyledTable';
import StatusBadge from '../../../components/ui/StatusBadge';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { workflowAutomationService } from '../../../services/workflowAutomationService';

const WorkflowJobsModal = ({ isOpen, onClose }) => {
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadJobs();
    }
  }, [isOpen]);

  const loadJobs = async () => {
    setIsLoading(true);
    try {
      const data = await workflowAutomationService?.getWorkflowJobs();
      setJobs(data);
    } catch (error) {
      console.error('Error loading jobs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getJobTypeLabel = (jobType) => {
    const labels = {
      batch_contract_processing: 'معالجة دفعة العقود',
      bulk_document_generation: 'إنشاء مستندات متعددة',
      automated_follow_up: 'جدولة متابعات تلقائية',
      signature_reminder_batch: 'تذكيرات التوقيع',
      contract_review_batch: 'مراجعة العقود'
    };
    return labels?.[jobType] || jobType;
  };

  const calculateProgress = (job) => {
    if (job?.totalItems === 0) return 0;
    return Math.round((job?.processedItems / job?.totalItems) * 100);
  };

  const stats = [
    {
      label: 'الإجمالي',
      value: jobs?.length,
      icon: 'Inbox',
      iconColor: '#3B82F6',
      gradient: 'from-blue-500/20 to-blue-600/10',
      border: 'border-blue-500/30',
      labelColor: 'text-blue-400',
      valueColor: 'text-blue-400',
    },
    {
      label: 'مكتمل',
      value: jobs?.filter(j => j?.status === 'completed')?.length,
      icon: 'CheckCircle',
      iconColor: '#10B981',
      gradient: 'from-green-500/20 to-green-600/10',
      border: 'border-green-500/30',
      labelColor: 'text-green-400',
      valueColor: 'text-green-400',
    },
    {
      label: 'جاري',
      value: jobs?.filter(j => j?.status === 'running')?.length,
      icon: 'Clock',
      iconColor: '#F59E0B',
      gradient: 'from-yellow-500/20 to-yellow-600/10',
      border: 'border-yellow-500/30',
      labelColor: 'text-yellow-400',
      valueColor: 'text-yellow-400',
    },
    {
      label: 'فاشل',
      value: jobs?.filter(j => j?.status === 'failed')?.length,
      icon: 'XCircle',
      iconColor: '#EF4444',
      gradient: 'from-red-500/20 to-red-600/10',
      border: 'border-red-500/30',
      labelColor: 'text-red-400',
      valueColor: 'text-red-400',
    },
  ];

  const columns = [
    {
      header: 'اسم المهمة',
      accessor: 'jobName',
      className: 'w-1/4',
      render: (row) => (
        <p className="text-foreground font-semibold truncate">{row?.jobName}</p>
      ),
    },
    {
      header: 'النوع',
      accessor: 'jobType',
      className: 'w-1/6',
      render: (row) => (
        <p className="text-muted-foreground text-xs truncate">{getJobTypeLabel(row?.jobType)}</p>
      ),
    },
    {
      header: 'الحالة',
      accessor: 'status',
      className: 'w-1/6',
      render: (row) => <StatusBadge status={row?.status} label={row?.status} icon="Circle" className="inline-flex" />,
    },
    {
      header: 'التقدم',
      accessor: 'progress',
      className: 'w-1/5',
      render: (row) => (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{calculateProgress(row)}%</span>
          </div>
          <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-cyan-500 transition-all duration-300"
              style={{ width: `${calculateProgress(row)}%` }}
            />
          </div>
        </div>
      ),
    },
    {
      header: 'التاريخ',
      accessor: 'createdAt',
      className: 'w-1/6',
      render: (row) => (
        <p className="text-muted-foreground text-xs">
          {new Date(row?.createdAt)?.toLocaleDateString('ar-JO', { month: 'short', day: 'numeric' })}
        </p>
      ),
    },
    {
      header: 'تفاصيل',
      accessor: 'details',
      className: 'w-16 text-center',
      render: (row) => (
        <button
          onClick={(e) => {
            e?.stopPropagation();
            setSelectedJob(selectedJob?.id === row?.id ? null : row);
          }}
          className="p-1 hover:bg-muted rounded transition-colors"
        >
          <Icon
            name={selectedJob?.id === row?.id ? 'ChevronUp' : 'ChevronDown'}
            size={16}
            color="var(--color-muted-foreground)"
          />
        </button>
      ),
    },
  ];

  const footer = (
    <div className="flex items-center justify-between">
      <Button
        variant="outline"
        iconName="RefreshCw"
        iconPosition="right"
        onClick={loadJobs}
        disabled={isLoading}
      >
        تحديث
      </Button>
      <Button variant="default" onClick={onClose}>
        إغلاق
      </Button>
    </div>
  );

  return (
    <StyledModal
      isOpen={isOpen}
      onClose={onClose}
      title="سجل مهام الأتمتة"
      size="xl"
      variant="cyan"
      showStats={true}
      stats={stats}
      footer={footer}
      icon="Inbox"
      iconColor="#06B6D4"
      className=""
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={32} color="var(--color-primary)" className="animate-spin" />
        </div>
      ) : (
        <div className="space-y-2">
          <StyledTable
            columns={columns}
            data={jobs}
            variant="cyan"
            emptyMessage="لا توجد مهام أتمتة حالياً"
            emptyIcon="Inbox"
            onRowClick={() => {}}
            selectedRow={null}
            onSelectRow={() => {}}
            onSelectAll={() => {}}
            className=""
          />

          {/* Expanded Details */}
          {selectedJob && (
            <div className="mt-3 p-4 bg-muted/30 rounded-lg border border-border">
              <h4 className="text-sm font-semibold text-foreground mb-3">تفاصيل المهمة</h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-muted/30 rounded p-3">
                  <p className="text-xs text-muted-foreground mb-1">الإجمالي</p>
                  <p className="text-sm font-semibold text-foreground">{selectedJob?.totalItems}</p>
                </div>
                <div className="bg-green-500/10 rounded p-3">
                  <p className="text-xs text-green-400 mb-1">ناجح</p>
                  <p className="text-sm font-semibold text-green-400">
                    {selectedJob?.processedItems - (selectedJob?.failedItems || 0)}
                  </p>
                </div>
                <div className="bg-red-500/10 rounded p-3">
                  <p className="text-xs text-red-400 mb-1">فاشل</p>
                  <p className="text-sm font-semibold text-red-400">{selectedJob?.failedItems || 0}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </StyledModal>
  );
};

export default WorkflowJobsModal;